import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Users, CheckCircle, XCircle, TrendingUp, DollarSign, AlertCircle, RefreshCw,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { CompactStat } from '@/components/data-display/CompactStat';
import { ROUTES } from '@/constants';
import { useScoring } from '../../hooks/useScoring';
import { formatCurrency, formatNumber } from '@/utils/formatters';
import type { ProfilesListFilters, ProfileSortBy, ProfileSortOrder } from '@/types/scoring.types';
import { CandidatosFilters } from '../../components/CandidatosFilters';
import { CandidatosList } from '../../components/CandidatosList';
import { CandidatoDetailPanel } from '../../components/CandidatoDetailPanel';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Originacion', path: ROUTES.PIPELINE_MATRIX },
  { label: 'Scoring', path: ROUTES.SCORING },
  { label: 'Candidatos a Credito', path: ROUTES.CREDIT_CANDIDATOS },
];

function filtersToParams(f: ProfilesListFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.is_eligible) p.set('eligible', f.is_eligible);
  if (f.credit_level) p.set('level', f.credit_level);
  if (f.banner_state) p.set('bstate', f.banner_state);
  if (f.sort_by && f.sort_by !== 'score') p.set('sort', f.sort_by);
  if (f.sort_order && f.sort_order !== 'desc') p.set('order', f.sort_order);
  if (f.page && f.page > 1) p.set('page', String(f.page));
  if (f.search) p.set('q', f.search);
  return p;
}

function parseFiltersFromURL(params: URLSearchParams): ProfilesListFilters {
  return {
    is_eligible: params.get('eligible') ?? undefined,
    credit_level: params.get('level') ?? undefined,
    banner_state: params.get('bstate') ?? undefined,
    sort_by: (params.get('sort') as ProfileSortBy) ?? 'score',
    sort_order: (params.get('order') as ProfileSortOrder) ?? 'desc',
    page: Number(params.get('page')) || 1,
    page_size: 50,
    search: params.get('q') ?? undefined,
  };
}

export function CandidatosPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const goToScoring = () => navigate(ROUTES.SCORING);
  const filters = useMemo(() => parseFiltersFromURL(searchParams), [searchParams]);

  const { profiles, pagination, summary, isSummaryLoading, isLoading, isError, refetch } = useScoring(filters);

  const selectedDirectoryId = searchParams.get('selected');

  const selectedProfile = useMemo(
    () => profiles.find((p) => p.directory_id === selectedDirectoryId) ?? profiles[0] ?? null,
    [profiles, selectedDirectoryId],
  );

  function handleFiltersChange(next: ProfilesListFilters) {
    const params = filtersToParams(next);
    if (selectedDirectoryId) params.set('selected', selectedDirectoryId);
    setSearchParams(params);
  }

  function handleSelect(directoryId: string) {
    const next = new URLSearchParams(searchParams);
    next.set('selected', directoryId);
    setSearchParams(next, { replace: true });
  }

  function handleSortChange(by: ProfileSortBy, order: ProfileSortOrder) {
    handleFiltersChange({ ...filters, sort_by: by, sort_order: order, page: 1 });
  }

  function handlePageChange(page: number) {
    handleFiltersChange({ ...filters, page });
  }

  const total = summary?.total_profiles ?? 0;
  const eligible = summary?.eligible_count ?? 0;
  const ineligible = summary?.ineligible_count ?? 0;
  const avgScore = summary?.avg_score ?? 0;
  const totalPotential = summary?.pipeline?.total_potential_mxn ?? 0;
  const eligiblePct = total > 0 ? `${((eligible / total) * 100).toFixed(1)}%` : '0%';
  const ineligiblePct = total > 0 ? `${((ineligible / total) * 100).toFixed(1)}%` : '0%';

  return (
    <div>
      <PageHeader
        title="Candidatos a Credito"
        helpKey="scoring"
        subtitle="Lista detallada de perfiles evaluados, elegibles y no elegibles"
        breadcrumbs={BREADCRUMBS}
        actions={
          <Button onClick={refetch}>
            <RefreshCw size={16} />
            Actualizar
          </Button>
        }
      />

      <div className="space-y-4">
        {isSummaryLoading ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {Array.from({ length: 5 }, (_, i) => <Skeleton key={i} height={56} variant="rectangular" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <CompactStat title="Total" value={formatNumber(total)} icon={Users} onClick={goToScoring} />
            <CompactStat
              title="Elegibles"
              value={formatNumber(eligible)}
              subtitle={eligiblePct}
              icon={CheckCircle}
              iconAccent="green"
              onClick={goToScoring}
            />
            <CompactStat
              title="No Elegibles"
              value={formatNumber(ineligible)}
              subtitle={ineligiblePct}
              icon={XCircle}
              iconAccent="red"
              onClick={goToScoring}
            />
            <CompactStat title="Score Prom." value={avgScore.toFixed(1)} icon={TrendingUp} onClick={goToScoring} />
            <CompactStat
              title="Pot. Total"
              value={formatCurrency(totalPotential, 'MXN', 'es-MX')}
              icon={DollarSign}
              iconAccent="green"
              onClick={goToScoring}
            />
          </div>
        )}

        {/* Skeleton solo en primer load (cuando aun no hay perfiles).
            Refetchs sucesivos (search, paginacion) NO desmontan los filtros — esto evita perdida de foco. */}
        {isLoading && profiles.length === 0 && <Skeleton height={400} variant="rectangular" />}

        {isError && (
          <Card>
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <AlertCircle size={32} className="text-destructive" />
              <h3 className="text-lg font-semibold text-foreground">Error al cargar candidatos</h3>
              <Button variant="primary" onClick={refetch}>Reintentar</Button>
            </div>
          </Card>
        )}

        {!isError && (profiles.length > 0 || !isLoading) && (
          <Card>
            <div className="space-y-3">
              <CandidatosFilters
                filters={filters}
                summary={summary}
                onFiltersChange={handleFiltersChange}
              />

              <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.4fr_1fr]">
                <div className="overflow-hidden rounded-md border border-border">
                  <div className="flex items-center justify-between border-b border-border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
                    <span>
                      Mostrando {profiles.length} de {pagination.total_items}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        disabled={pagination.page <= 1}
                        onClick={() => handlePageChange(pagination.page - 1)}
                        className="rounded border border-border bg-card px-2 py-0.5 text-foreground disabled:opacity-30"
                      >
                        ‹
                      </button>
                      <span>Pag. {pagination.page} de {pagination.total_pages}</span>
                      <button
                        disabled={pagination.page >= pagination.total_pages}
                        onClick={() => handlePageChange(pagination.page + 1)}
                        className="rounded border border-border bg-card px-2 py-0.5 text-foreground disabled:opacity-30"
                      >
                        ›
                      </button>
                    </div>
                  </div>
                  <CandidatosList
                    profiles={profiles}
                    selectedDirectoryId={selectedProfile?.directory_id ?? null}
                    sortBy={filters.sort_by ?? 'score'}
                    sortOrder={filters.sort_order ?? 'desc'}
                    onSelect={handleSelect}
                    onSortChange={handleSortChange}
                  />
                </div>

                <div className="rounded-md border border-border bg-card p-4">
                  <CandidatoDetailPanel profile={selectedProfile} />
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
